import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Check, Search, Image, Loader2 } from "lucide-react";
import { formatCurrency } from "../../lib/utils";
import api from "../../lib/api";
import { toast } from "sonner";

export default function DishPickerDialog({ open, onClose, onOpenChange, menuId, categoryId, categoryName, onAdded, onLinked }) {
  const handleClose = () => {
    if (onClose) onClose();
    else if (onOpenChange) onOpenChange(false);
  };
  const notifyLinked = onAdded || onLinked;
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (open && menuId) {
      setLoading(true);
      setSelected(new Set());
      setSearch("");
      api.getAvailableDishes(menuId)
        .then(data => setDishes(data.dishes || []))
        .catch(() => toast.error("Error al cargar platos"))
        .finally(() => setLoading(false));
    }
  }, [open, menuId]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return dishes.filter(d =>
      !d.already_linked &&
      (d.name.toLowerCase().includes(q) || d.category_name.toLowerCase().includes(q) || d.menu_name.toLowerCase().includes(q))
    );
  }, [dishes, search]);

  const grouped = useMemo(() => {
    const map = {};
    for (const d of filtered) {
      const key = d.menu_name || "Otros";
      if (!map[key]) map[key] = [];
      map[key].push(d);
    }
    return map;
  }, [filtered]);

  const toggle = (dishId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(dishId)) next.delete(dishId); else next.add(dishId);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    try {
      await api.addDailyMenuItems(menuId, [...selected], categoryId);
      toast.success(`${selected.size} plato${selected.size > 1 ? "s" : ""} vinculado${selected.size > 1 ? "s" : ""}`);
      notifyLinked?.();
      handleClose();
    } catch (err) {
      toast.error(err.message || "Error al vincular platos");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Seleccionar platos para "{categoryName}"</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar plato..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10"
            data-testid="dish-picker-search"
          />
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-4 pr-1" style={{ maxHeight: "50vh" }}>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : Object.keys(grouped).length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No hay platos disponibles</p>
          ) : (
            Object.entries(grouped).map(([menuName, menuDishes]) => (
              <div key={menuName}>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 sticky top-0 bg-background py-1">
                  {menuName}
                </p>
                <div className="space-y-1">
                  {menuDishes.map(dish => {
                    const isSelected = selected.has(dish.dish_id);
                    return (
                      <button
                        key={dish.dish_id}
                        onClick={() => toggle(dish.dish_id)}
                        data-testid={`pick-dish-${dish.dish_id}`}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg border transition-all text-left ${
                          isSelected
                            ? "bg-primary/10 border-primary"
                            : "bg-background border-border hover:border-primary/30"
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          isSelected ? "bg-primary border-primary" : "border-muted-foreground/30"
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                        </div>
                        {dish.has_image && <Image className="w-4 h-4 text-green-500 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{dish.name}</p>
                          <p className="text-xs text-muted-foreground">{dish.category_name}</p>
                        </div>
                        <span className="text-sm font-medium text-muted-foreground">{formatCurrency(dish.price)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex items-center justify-between border-t pt-3">
          <span className="text-sm text-muted-foreground">
            {selected.size > 0 ? `${selected.size} seleccionado${selected.size > 1 ? "s" : ""}` : "Ninguno seleccionado"}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose}>Cancelar</Button>
            <Button onClick={handleAdd} disabled={saving || selected.size === 0} data-testid="dish-picker-add-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Vincular platos
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
